/**
 * AD-007h's produced turn: the original does not derive a heading rate from
 * coordinated-turn lift geometry, it *produces* one from bank angle and
 * elevator pull. Split out of `controls.ts` when AD-007l's fitted bank shape
 * pushed that file past the 400-line gate — the same per-module pattern
 * `aero-tunables.ts` and `stall-tunables.ts` came out on. No values moved in
 * the move.
 *
 * This is a fourth, separate rate from the three body axes `controls.ts`
 * commands: it curves the flight path and carries the nose with it (`forces.ts`
 * reads it, `step.ts` applies it kinematically), and it is not a body rate.
 */

import { postStallFactor, type AeroSpec } from "./aero.ts";
import type { TurnSpec } from "./controls.ts";
import type { Tunables } from "./tunables.ts";
import { clamp, degreesToRadians } from "./units.ts";


/** The directional sustained-turn-rate ceiling from `.DAT` offsets 116/118, before the measured cap-gain correction (see `producedHeadingRateRadPerSec`). */
export function turnRateLimit(turn: TurnSpec, turningRight: boolean): number {
  return turningRight ? turn.rightRadPerSec : turn.leftRadPerSec;
}

export interface TurnRateDemand {
  readonly turn: TurnSpec;
  readonly aero: AeroSpec;
  readonly tunables: Tunables;
  /** Signed bank angle, radians, right-positive — selects the turn's direction and most of its magnitude. */
  readonly bankRad: number;
  /** Elevator stick position, −1..1 — only back pressure (positive) adds to the demand. */
  readonly pitchInput: number;
  readonly alphaRad: number;
  /** Novice's bank-to-turn assist (AD-007b): auto-coordination, so the demand does not need real back pressure to reach its ceiling. */
  readonly bankToTurnAssist: boolean;
}

export interface TurnRateResult {
  /** Produced heading rate this tick, rad/s, right-positive. */
  readonly rateRadPerSec: number;
  /** True once bank plus pull have driven the demand to the directional ceiling. */
  readonly atCeiling: boolean;
}

/**
 * AD-007h: the original *produces* heading rate from bank and elevator pull —
 * it does not derive one from coordinated-turn lift geometry. Bank angle sets
 * the turn's direction and, on its own, a fraction of its magnitude; back
 * pressure tops the rest up (`turnRatePullGain`), which is the manual's own line
 * for Intermediate/Expert — bank without back pressure degenerates into the
 * spiral dive (spin.ts) instead of a turn. Novice's bank-to-turn assist supplies
 * that back pressure automatically (no nose-drop, no pull required), which is
 * the *same* mechanism as the higher tiers, only auto-coordinated. Past the
 * stall break the demand collapses along the same curve the wing's own lift does
 * (`postStallFactor`) — a departed wing is not producing a turn.
 *
 * **How much of the turn the bank owns is AD-007l's correction.** Bank alone
 * rises to `turnRateBankShare` (0.70) of the ceiling and saturates there; full
 * back pressure buys the remaining 0.30. That is thirteen settled hands-off
 * banks in flight-tunables.md §8.5.1 — flat at 22.7 °/s from 41° through 61°,
 * against §4.4's 32.6 °/s sustained *with* the pull. AD-007h had bank alone
 * reaching the whole ceiling by 45° with the pull stacked on top.
 *
 * **The shape between zero and the plateau is a quarter-sine, and it is fitted
 * rather than chosen.** §8.5.1's sub-knee readings are concave — 0.740 of the
 * plateau at 25.75° where a linear ramp anchored on the plateau gives 0.55 — so
 * the shape matters as much as the normalisation. Five one-parameter shapes were
 * fitted by least squares to the twelve settled banks of §8.5.1 **with §8.1's
 * 5.3 °/s at 7° held out**, and then asked to predict it:
 *
 * | shape | RMS (°/s) | predicts at 7° |
 * |---|---:|---:|
 * | linear ramp | 0.571 | 4.24 (−20 %) |
 * | power ramp, p = 0.68 | 0.345 | 7.05 (+33 %) |
 * | **quarter-sine, k = 46.4°** | **0.387** | **5.32 (+0.4 %)** |
 * | quadratic, flat top | 0.465 | 6.11 (+15 %) |
 * | 1 − exp(−φ/k) | 0.929 | 9.18 (+73 %) |
 *
 * The quarter-sine reproduces a measurement it never saw, from a different
 * session and a different procedure, to better than half a percent. That is
 * cross-validation, and it is why §8.1's 7° datum is *corroborated* by session 3
 * rather than superseded by it: the two sit on one curve.
 *
 * **The pull rides the bank's own ramp, and that is not cosmetic.** The demand
 * is `ramp · (share + pull)`, not `share·ramp + pull`: back pressure tightens the
 * turn the *bank* has set up and cannot produce one on its own, which is both
 * the manual's description and what §8.5.4 measures — a wings-level full-back
 * pull yaws the original's Camel by precession alone, a couple of degrees a
 * second, not by a third of its turn ceiling. AD-007h's additive form said
 * otherwise and hid it behind an exact `bankRad === 0` early return; once
 * AD-007m's precession perturbs the bank off *exactly* zero by a rounding
 * error, that knife-edge starts emitting 10 °/s of turn with the sign of the
 * last floating-point bit. Both of AD-007h's own anchors are untouched by the
 * change (bank alone is `ramp·share` either way, and full back stick past the
 * knee is the ceiling either way); what moves is the mid-bank pull, which is
 * reported in AD-007l's implementation note.
 *
 * `turnRateCapGain` corrects the `.DAT` 116/118 fields' raw ÷30 ceiling up to
 * what the original actually sustains (flight-tunables.md §8.1: measured
 * 32.6/26.7 °/s on the Camel against 31.0/23.3 °/s raw).
 */
export function producedHeadingRateRadPerSec(demand: TurnRateDemand): TurnRateResult {
  const { turn, aero, tunables, bankRad, pitchInput, alphaRad, bankToTurnAssist } = demand;
  if (bankRad === 0) return { rateRadPerSec: 0, atCeiling: false };

  const turningRight = bankRad > 0;
  const ceiling = turnRateLimit(turn, turningRight) * tunables.turnRateCapGain;
  const bankRamp = Math.sin(
    (Math.PI / 2) * clamp(Math.abs(bankRad) / degreesToRadians(tunables.turnRateBankSaturationDeg), 0, 1),
  );
  const pullFraction = bankToTurnAssist
    ? tunables.turnRatePullGain
    : clamp(pitchInput, 0, 1) * tunables.turnRatePullGain;
  const demandFraction = clamp(bankRamp * (tunables.turnRateBankShare + pullFraction), 0, 1);

  return {
    rateRadPerSec: Math.sign(bankRad) * ceiling * demandFraction * postStallFactor(aero, alphaRad),
    atCeiling: demandFraction >= 1,
  };
}
